import {
  Box,
  BoxProps,
  CardProps,
  Tooltip,
  TooltipProps,
  Typography,
} from "@mui/material";
import { FC, type JSX } from "react";

import {
  OverviewTableCell,
  OverviewTableCellHeader,
} from "../overview-table-cell";

export interface TextCardCellProps {
  /** cell header */
  header?: string;
  /** cell body */
  body: string;
  /** Size of the body text. Options: "normal" (default) or "small" */
  bodyTextSize?: "normal" | "small";
  /** After how many characters should the body be cut.  */
  cutBodyAfter?: number;
  /** Whether to automatically cut the body text based on the cell width. Overrides `cutBodyAfter` if set to true. */
  autoCutBody?: boolean;
  /** Additional MUI styling */
  cardProps?: CardProps;
  /** Additional header styling */
  headerProps?: BoxProps;
  /** Additional header styling */
  bodyProps?: BoxProps;
  /** Show tooltip on hover */
  tooltipShow?: boolean;
  /** Tooltip content */
  tooltipTitle?: string;
  /** Custom tooltip props */
  tooltipProps?: Partial<TooltipProps>;
  /** End icon */
  endIcon?: JSX.Element;
  /** Icon to display before the header */
  icon?: JSX.Element;
  /** onClick function for the entire cell */
  onClick?: () => void;
  //isHidden not used in here used to hide sells in the pimo table
  isHidden?: boolean;
  textColor?: string;
}

/** Text Card with title and body for the text only cells */
export const TextCardCell: FC<TextCardCellProps> = ({
  header,
  body,
  bodyTextSize = "normal",
  cutBodyAfter,
  autoCutBody = false,
  cardProps,
  headerProps = {},
  bodyProps = {},
  tooltipShow = false,
  tooltipTitle,
  tooltipProps,
  endIcon,
  icon,
  onClick,
  textColor,
}) => {
  const cutBody =
    !autoCutBody && cutBodyAfter !== undefined && body.length > cutBodyAfter;

  return (
    <>
      {tooltipShow ? (
        <Tooltip
          PopperProps={{
            disablePortal: true,
          }}
          disableInteractive
          followCursor
          title={tooltipTitle}
          componentsProps={{
            tooltip: {
              sx: {
                "& .MuiTooltip-arrow": {
                  color: "white",
                },
                backgroundColor: "#fff",
                color: "#414141",
                border: "1px solid",
                borderColor: "background.paper",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                fontSize: "14px",
                fontWeight: 400,
                padding: "12px 16px",
                borderRadius: "8px",
                whiteSpace: "pre-wrap",
              },
            },
          }}
        >
          <OverviewTableCell {...cardProps} onClick={onClick}>
            <OverviewTableCellHeader
              sx={{
                ...headerProps.sx,
              }}
            >
              {icon && (
                <Box sx={{ display: "inline", marginRight: 1 }}>{icon}</Box>
              )}
              {header}
            </OverviewTableCellHeader>
            <Tooltip title={tooltipProps ? body : undefined} {...tooltipProps}>
              <Typography
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  ...(bodyTextSize === "small" && {
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "18px",
                  }),
                  ...(autoCutBody && {
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    lineClamp: 2,
                    WebkitLineClamp: 2,
                  }),
                  color: textColor ?? "#414141",
                  ...bodyProps.sx,
                }}
              >
                {cutBody ? body.substring(0, cutBodyAfter) + "..." : body}
              </Typography>
            </Tooltip>
          </OverviewTableCell>
        </Tooltip>
      ) : (
        <OverviewTableCell {...cardProps} onClick={onClick}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box>
              <OverviewTableCellHeader
                sx={{
                  ...headerProps.sx,
                }}
              >
                {icon && (
                  <Box sx={{ display: "inline", marginRight: 1 }}>{icon}</Box>
                )}
                {header}
              </OverviewTableCellHeader>

              <Tooltip
                title={tooltipProps ? body : undefined}
                {...tooltipProps}
              >
                <Typography
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    ...(bodyTextSize === "small" && {
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "18px",
                    }),
                    ...(autoCutBody && {
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      lineClamp: 2,
                      WebkitLineClamp: 2,
                    }),
                    color: textColor ?? "#414141",
                    ...bodyProps.sx,
                  }}
                >
                  {cutBody ? body.substring(0, cutBodyAfter) + "..." : body}
                </Typography>
              </Tooltip>
            </Box>
            {endIcon}
          </Box>
        </OverviewTableCell>
      )}
    </>
  );
};

export default TextCardCell;
